import { clsx, type ClassValue } from "clsx";
import { extendTailwindMerge } from "tailwind-merge";

/**
 * Without this, tailwind-merge reads our named shadows as shadow *colors*
 * (the `shadow-red-500` shape) instead of box-shadow values, so `shadow-none`
 * fails to override `shadow-popover` and both end up in the output.
 */
const twMerge = extendTailwindMerge({
  extend: {
    classGroups: {
      shadow: [{ shadow: ["popover", "elevated"] }],
    },
  },
});

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function cleanFullName(name: string): string {
  if (!name) return "";

  const withoutEmojis = name.replace(/[^\p{L}\p{M}\s'-]/gu, "");

  const properCase = withoutEmojis
    .split(/\s+/)
    .filter((word) => word.length > 0)
    .map((word) => {
      return word
        .split("-")
        .map((part) => {
          return part.charAt(0).toUpperCase() + part.slice(1).toLowerCase();
        })
        .join("-");
    })
    .join(" ");

  return properCase.trim();
}

export async function copyToClipboard(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch (err) {
    console.error("Failed to copy to clipboard:", err);
    return false;
  }
}

export function formatNumber(number: number): string {
  if (number < 1000) {
    return Math.round(number).toString();
  } else if (number < 1000000) {
    return (number / 1000).toFixed(1) + "k";
  } else if (number < 1000000000) {
    return (number / 1000000).toFixed(1) + "M";
  } else {
    return (number / 1000000000).toFixed(1) + "B";
  }
}

export function formatCompactNumber(n: number): string {
  if (n === 0) return "0";
  if (n >= 1_000_000_000)
    return (n / 1_000_000_000).toFixed(1).replace(/\.0$/, "") + " Md";
  if (n >= 1_000_000)
    return (n / 1_000_000).toFixed(1).replace(/\.0$/, "") + " M";
  if (n >= 1_000) return (n / 1_000).toFixed(0) + " k";
  return n.toFixed(0);
}

export function formatYear(n: number): string {
  return String(n);
}
